import { MeasureTypes } from '@commutifi-fe/constants';
import { Location } from '@commutifi-fe/interfaces';
import { create, unitDependencies, evaluateDependencies } from 'mathjs';
import { isNil } from 'lodash';
import { Required } from 'utility-types';
export const getDistanceFromLatLngInKm = (originLocation: Location | undefined, destLocation: Location | undefined) => {
  if (!originLocation || !destLocation) {
    return 0;
  }
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(destLocation.lat - originLocation.lat);
  const dLng = deg2rad(destLocation.lng - originLocation.lng);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(deg2rad(originLocation.lat)) * Math.cos(deg2rad(destLocation.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d;
};
export const deg2rad = (deg: number) => {
  return deg * (Math.PI / 180);
};
export const limitNumberWithinRange = (num: number | string, min?: number, max?: number) => {
  const MIN = min || Number.MIN_VALUE;
  const MAX = max || Number.MAX_VALUE;
  const parsed = typeof num === 'string' ? parseInt(num) : num;
  return Math.min(Math.max(parsed, MIN), MAX);
};
export const isEven = (num: number) => num % 2 === 0;
export const calculatePercentage = (value: number, total: number) => {
  if (total === 0 || value === 0) {
    return 0;
  }
  return value / total * 100;
};
export const isNumber = (num: any) => num !== null && !isNaN(num) && num !== undefined;

/**
 * Type-safe wrapper around lodash isFinite that acts as a type guard.
 * Returns true if the value is a finite number (not NaN, Infinity, or undefined/null).
 * @param value - The value to check
 * @returns True if value is a finite number, with TypeScript narrowing
 */
export const isFiniteNumber = (value: number | undefined | null): value is number => value !== null && value !== undefined && Number.isFinite(value);
export const isConsecutiveNumbers = (numbers: (number | string)[]) => {
  if (!numbers) {
    return false;
  }
  if (numbers.length <= 1) {
    return true;
  }
  const differenceArray = numbers.slice(1).map((number, index) => {
    const currentNb = typeof number === 'string' ? parseInt(number) : number;
    const nextNb = numbers[index];
    const comparedNb = typeof nextNb === 'string' ? parseInt(nextNb) : nextNb;
    return currentNb - comparedNb;
  });
  return differenceArray.every(value => value === 1);
};

/**
 * Convert cents to dollars.
 * @param amount amount in cents
 * @returns amount in dollars (2 decimal places), or undefined if input is null/undefined
 */
export const centsToDollars = (amount?: number | null): number | undefined => {
  if (isNil(amount)) {
    return undefined;
  }
  return parseFloat((amount / 100).toFixed(2));
};

/**
 * Convert dollars to cents, truncating any fractional cents.
 * Uses Math.floor to avoid overcharging (fractional cents are dropped).
 * @param amount amount in dollars
 * @returns amount in cents (whole number, truncated), or undefined if input is null/undefined
 */
export const dollarsToCents = (amount?: number | null): number | undefined => {
  if (isNil(amount)) {
    return undefined;
  }
  return Math.floor(amount * 100);
};
export const metersToKilometers = (meters: number) => {
  return unit(meters, 'm').toNumber('km');
};
export const kilometersToMeters = (kilometers: number) => {
  return unit(kilometers, 'km').toNumber('m');
};
const {
  unit,
  evaluate
} = create({
  unitDependencies,
  evaluateDependencies
}, {}) as Required<Partial<math.MathJsInstance>, 'unit'>;
export const unitBaseToCommutifiMeasureType = (base: string): MeasureTypes | null => {
  switch (base) {
    case 'LENGTH':
      return MeasureTypes.Length;
    case 'MASS':
      return MeasureTypes.Mass;
    case 'TIME':
      return MeasureTypes.Time;
    // Be careful Liquid Volume isn't a distinct value in MathJS..
    case 'VOLUME':
      return MeasureTypes.Volume;
    // Measure not yet supported by Commutifi are returned as null
    case 'AMOUNT_OF_SUBSTANCE':
    case 'ANGLE':
    case 'BINARY':
    case 'CURRENT':
    case 'ELECTRIC_POTENTIAL':
    case 'ENERGY':
    case 'FORCE':
    case 'FREQUENCY':
    case 'LUMINOUS_INTENSITY':
    case 'POWER':
    case 'PRESSURE':
    case 'SURFACE':
    case 'TEMPERATURE':
    default:
      return null;
  }
};

// ─── Seeded PRNG & Hashing ───────────────────────────────────────────────────

const LCG_MULTIPLIER = 16807;
const LCG_MODULUS = 2147483647; // 2^31 - 1

/**
 * Creates a deterministic pseudo-random number generator (Park-Miller LCG)
 * seeded by the given integer. Each call to the returned function produces the
 * next value in the sequence (0-1 range, exclusive).
 *
 * Pair with `hashStringToInt` when your seed is a string:
 * ```ts
 * const rand = seededRandom(hashStringToInt('booking-abc'))
 * rand() // 0.417…  (always the same for 'booking-abc')
 * rand() // 0.823…
 * ```
 *
 * Common use cases:
 * - Skeleton loading placeholders with stable, "content-shaped" layouts
 * - Deterministic color/position assignment keyed by an entity ID
 * - Reproducible test data generation
 *
 * @param seed - A positive integer (use `hashStringToInt` to derive one from a string)
 * @returns A function that returns the next pseudo-random number (0-1) on each call
 */
export function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = s * LCG_MULTIPLIER % LCG_MODULUS;
    return (s - 1) / (LCG_MODULUS - 1);
  };
}
const HASH_PRIME = 31;

/**
 * Converts a string into a deterministic 32-bit positive integer using a
 * polynomial rolling hash (prime 31). Not cryptographic — intended for
 * bucketing, seeding PRNGs, or lightweight fingerprinting.
 *
 * ```ts
 * hashStringToInt('booking-abc') // 1234567 (stable across calls/renders)
 * ```
 *
 * Common use cases:
 * - Seed for `seededRandom` to get a string-keyed PRNG
 * - Lightweight deduplication fingerprint for bookings or entities
 * - Deterministic bucketing/partitioning by string key
 *
 * @param str - The input string to hash
 * @returns A non-negative 32-bit integer
 */
export function hashStringToInt(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(HASH_PRIME, h) + str.charCodeAt(i) | 0;
  }
  return Math.abs(h);
}
export { unit, evaluate };